"use strict";
var _a, _b, _c, _d, _e, _f;
Object.defineProperty(exports, "__esModule", { value: true });
exports.ConsultationService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const appointment_entity_1 = require("./appointment.entity");
const order_entity_1 = require("../orders/order.entity");
const order_line_item_entity_1 = require("../orders/order-line-item.entity");
const notifications_service_1 = require("../notifications/notifications.service");
const notification_log_entity_1 = require("../notifications/notification-log.entity");
const user_entity_1 = require("../users/user.entity");
const customer_entity_1 = require("../customers/customer.entity");
let ConsultationService = class ConsultationService {
    constructor(appointmentRepo, orderRepo, lineItemRepo, userRepo, customerRepo, notificationsService) {
        this.appointmentRepo = appointmentRepo;
        this.orderRepo = orderRepo;
        this.lineItemRepo = lineItemRepo;
        this.userRepo = userRepo;
        this.customerRepo = customerRepo;
        this.notificationsService = notificationsService;
    }
    findAll(customerId, doctorId, status) {
        const where = {};
        if (customerId)
            where.customer = { id: customerId };
        if (doctorId)
            where.doctor = { id: doctorId };
        if (status)
            where.status = status;
        return this.appointmentRepo.find({
            where,
            relations: ['customer', 'doctor', 'bookedByAgent'],
            order: { scheduledAt: 'ASC' },
        });
    }
    async findById(id) {
        const appt = await this.appointmentRepo.findOne({
            where: { id },
            relations: ['customer', 'doctor', 'bookedByAgent'],
        });
        if (!appt)
            throw new common_1.NotFoundException(`Appointment #${id} not found`);
        return appt;
    }
    /** Full detail load — includes customer medical docs and addresses for the consultation page. */
    async findByIdDetail(id) {
        const appt = await this.appointmentRepo.findOne({
            where: { id },
            relations: [
                'customer',
                'customer.medicalDocs',
                'customer.addresses',
                'doctor',
                'bookedByAgent',
            ],
        });
        if (!appt)
            throw new common_1.NotFoundException(`Appointment #${id} not found`);
        return appt;
    }
    async create(dto) {
        const doctor = await this.userRepo.findOne({ where: { id: dto.doctorId } });
        const customerRecord = await this.customerRepo.findOne({ where: { id: dto.customerId } });
        const consultationFee = this.resolveDoctorConsultationFee(doctor, dto.consultationType);
        const priorityFee = dto.isPriority ? Number(doctor?.priorityConsultationFee ?? 0) : 0;
        const totalFee = consultationFee + priorityFee;
        const appt = this.appointmentRepo.create({
            customer: { id: dto.customerId },
            doctor: { id: dto.doctorId },
            bookedByAgent: { id: dto.bookedByAgentId },
            scheduledAt: dto.scheduledAt,
            durationMinutes: dto.durationMinutes,
            consultationType: dto.consultationType,
            agentNotes: dto.agentNotes,
            isPriority: !!dto.isPriority,
            consultationFee,
            priorityFee,
            totalFee,
            paymentRequestedAt: new Date(),
            status: appointment_entity_1.AppointmentStatus.PAYMENT_PENDING,
        });
        const saved = await this.appointmentRepo.save(appt);
        const channels = dto.channels?.length ? dto.channels : [notification_log_entity_1.NotificationChannel.SMS];
        const notifications = (customerRecord?.phone || customerRecord?.email)
            ? await this.notificationsService.send({
                channels,
                to: {
                    phone: customerRecord.phone,
                    email: customerRecord.email,
                    name: customerRecord.name,
                    customerId: dto.customerId,
                },
                template: notification_log_entity_1.NotificationTemplate.PAYMENT_DETAILS,
                variables: {
                    customerName: customerRecord.name ?? 'Customer',
                    amount: totalFee.toFixed(2),
                    upiId: process.env['CONSULTATION_UPI_ID'] ?? process.env['DEFAULT_UPI_ID'] ?? 'vaidban@upi',
                },
                referenceId: saved.id,
                referenceType: 'appointment',
                sentById: dto.bookedByAgentId,
            })
            : [];
        return { appointment: await this.findById(saved.id), notifications };
    }
    async updateStatus(id, status, reason) {
        const appt = await this.findById(id);
        appt.status = status;
        if (reason)
            appt.cancelReason = reason;
        return this.appointmentRepo.save(appt);
    }
    async reschedule(id, newScheduledAt, agentId) {
        const appt = await this.findById(id);
        appt.scheduledAt = newScheduledAt;
        appt.status = appt.paymentReceivedAt ? appointment_entity_1.AppointmentStatus.RESCHEDULED : appointment_entity_1.AppointmentStatus.PAYMENT_PENDING;
        appt.bookedByAgent = { id: agentId };
        return this.appointmentRepo.save(appt);
    }
    async markPaymentReceived(id, paymentReference) {
        const appt = await this.findById(id);
        appt.paymentReceivedAt = new Date();
        appt.paymentReference = paymentReference?.trim() || null;
        appt.status = appointment_entity_1.AppointmentStatus.CONFIRMED;
        return this.appointmentRepo.save(appt);
    }
    async addDoctorNotes(id, notes, _doctorId) {
        const appt = await this.findById(id);
        appt.doctorOutcomeNotes = notes;
        appt.status = appointment_entity_1.AppointmentStatus.COMPLETED;
        return this.appointmentRepo.save(appt);
    }
    async markWalkIn(id) {
        const appt = await this.findById(id);
        appt.status = appointment_entity_1.AppointmentStatus.WALK_IN;
        return this.appointmentRepo.save(appt);
    }
    /**
     * Close a consultation:
     * 1. Save prescription HTML + medicine cart snapshot on the appointment.
     * 2. If medicines were prescribed, create a DRAFT order — appears in agent queue immediately.
     * 3. Mark appointment COMPLETED with closedAt timestamp.
     */
    async closeConsultation(id, dto, doctorId) {
        const appt = await this.findById(id);
        let orderId;
        let orderNumber;
        if (dto.prescriptionItems?.length) {
            const year = new Date().getFullYear();
            const count = await this.orderRepo.count();
            orderNumber = `ORD-${year}-${String(count + 1).padStart(5, '0')}`;
            const subtotal = dto.prescriptionItems.reduce((s, i) => s + i.lineTotal, 0);
            const order = this.orderRepo.create({
                orderNumber,
                customer: { id: appt.customer.id },
                shippingAddress: dto.shippingAddressId ? { id: dto.shippingAddressId } : undefined,
                createdByAgent: { id: doctorId },
                source: 'consultation',
                priority: 'standard',
                status: order_entity_1.OrderStatus.DRAFT,
                subtotal,
                totalAmount: subtotal,
                notes: `Generated from consultation #${id}. Awaiting agent confirmation.`,
            });
            const savedOrder = await this.orderRepo.save(order);
            orderId = savedOrder.id;
            for (const item of dto.prescriptionItems) {
                await this.lineItemRepo.save(this.lineItemRepo.create({
                    order: { id: savedOrder.id },
                    product: { id: item.productId },
                    quantity: item.quantity,
                    unitPrice: item.unitPrice,
                    lineTotal: item.lineTotal,
                }));
            }
        }
        appt.prescription = dto.prescription;
        appt.prescriptionItems = dto.prescriptionItems ?? [];
        appt.doctorOutcomeNotes = dto.outcomeNotes ?? appt.doctorOutcomeNotes;
        appt.status = appointment_entity_1.AppointmentStatus.COMPLETED;
        appt.closedAt = new Date();
        if (orderId)
            appt.generatedOrderId = orderId;
        const saved = await this.appointmentRepo.save(appt);
        return { appointment: saved, orderId, orderNumber };
    }
    resolveDoctorConsultationFee(doctor, type) {
        const fees = doctor?.consultationFees ?? {};
        return Number(fees[type] ?? 0);
    }
};
exports.ConsultationService = ConsultationService;
exports.ConsultationService = ConsultationService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(appointment_entity_1.Appointment)),
    tslib_1.__param(1, (0, typeorm_1.InjectRepository)(order_entity_1.Order)),
    tslib_1.__param(2, (0, typeorm_1.InjectRepository)(order_line_item_entity_1.OrderLineItem)),
    tslib_1.__param(3, (0, typeorm_1.InjectRepository)(user_entity_1.User)),
    tslib_1.__param(4, (0, typeorm_1.InjectRepository)(customer_entity_1.Customer)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _b : Object, typeof (_c = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _c : Object, typeof (_d = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _d : Object, typeof (_e = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _e : Object, typeof (_f = typeof notifications_service_1.NotificationsService !== "undefined" && notifications_service_1.NotificationsService) === "function" ? _f : Object])
], ConsultationService);
